import { GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";

/** CSS affecting the {@link TextComponent} */
const styles: string = css`
    .simon-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    .simon-grid {
        display: grid;
        grid-template-columns: repeat(2, 100px);
        grid-template-rows: repeat(2, 100px);
        gap: 10px;
        margin-bottom: 1em;
    }
    
    .simon-grid > .simon-button:nth-child(1) {
        border-top-left-radius: 100%;
    }

    .simon-grid > .simon-button:nth-child(2) {
        border-top-right-radius: 100%;
    }

    .simon-grid > .simon-button:nth-child(3) {
        border-bottom-left-radius: 100%;
    }

    .simon-grid > .simon-button:nth-child(4) {
        border-bottom-right-radius: 100%;
    }

    .simon-button {
        width: 100%;
        height: 100%;
        border: none;
        opacity: 0.5;
        cursor: pointer;
    }

    .simon-red {
        background-color: red;
    }

    .simon-green {
        background-color: green;
    }

    .simon-blue {
        background-color: blue;
    }

    .simon-yellow {
        background-color: yellow;
    }

    .flash {
        opacity: 1;
        scale: 1.05;
    }

    .button {
        font-family: monospace;
        font-size: 1em;
        background-color: black;
        border: 0;
        color: white;
        padding: 10px 20px;
        border-radius: 5px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
    }

    #simon-reset:hover {
        background-color: #332c57;
    }
`;

/**
 * Represents a simon says puzzle with 4 buttons.
 */
export class SimonPuzzleComponent extends HTMLElement {
    /** The number of steps that the player must complete to win the game */
    public steps: number = 5;

    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** The alias of the puzzle set by the canvas component */
    private _puzzleAlias: string = this.dataset.puzzle ?? "undefined";

    /**
     * The colors that the Simon game can use
     * @remarks The only colors supported are red, green, blue and yellow. Order does not matter.
     */
    private readonly _colors: string[] = ["red", "green", "blue", "yellow"];

    /** The sequence of colors that the player must repeat. Will be randomized each round. */
    private _sequence: string[] = [];

    /** The sequence of colors that the player has inputted. */
    private _playerSequence: string[] = [];

    /** The current step that the player is on */
    private _currentStep: number = 1;

    /** Whether a color is being flashed */
    private _flashingColor: boolean = false;

    /** The paragraph element that shows the status of the game */
    private _statusParagraph?: HTMLParagraphElement;

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        this.render();
    }

    /**
     * Generates a new sequence of colors for the player to repeat and sets it
     */
    private generateSequence(): void {
        this._sequence = [];

        for (let i: number = 0; i < this.steps; i++) {
            this._sequence.push(this._colors[Math.floor(Math.random() * this._colors.length)]);
        }
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
            <div class="simon-container">
                <p>Herhaal de volgende kleuren in de juiste volgorde</p>

                <div class="simon-grid">
                    ${this._colors.map(color => html`
                        <button class="simon-button simon-${color}" data-color="${color}"></button>
                    `)}
                </div>

                <div>
                    <button class="button" id="simon-reset">Starten</button>
                </div>

                <p id="simon-status"></p>
            </div>
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);

        // Add the event listeners for all the buttons
        this.shadowRoot.querySelectorAll(".simon-button").forEach(button => {
            button.addEventListener("click", async (e: Event) => {
                const button: HTMLButtonElement = e.target as HTMLButtonElement;

                if (button.dataset.color) {
                    await this.handlePlayerInput(button.dataset.color);
                }
                else {
                    console.error("Button has no color?!?!", button);
                }
            });
        });

        const resetButton: HTMLButtonElement | null = this.shadowRoot.querySelector("#simon-reset");
        resetButton?.addEventListener("click", () => {
            resetButton.innerText = "Probeer opnieuw";
            this.resetGame();
        });

        this._statusParagraph = this.shadowRoot.querySelector("#simon-status") ?? undefined;
    }

    /**
     * Play the sequence of colors for the player to repeat.
     */
    private async playSequence(): Promise<void> {
        for (let i: number = 0; i < this._currentStep; i++) {
            await this.flashColor(this._sequence[i]);
        }
    }

    /**
     * Flashes the button with the given color
     * @param color The color of the button to flash
     */
    private async flashColor(color: string): Promise<void> {
        if (!this.shadowRoot) {
            return;
        }

        const button: HTMLButtonElement | null = this.shadowRoot.querySelector(`button[data-color="${color}"]`);

        if (!button) {
            console.error("Could not find button with color", color);
            return;
        }

        // Disable player input while the sequence is playing
        this._flashingColor = true;

        button.classList.add("flash");

        // Wait for 500ms.
        await new Promise(resolve => setTimeout(resolve, 500));

        button.classList.remove("flash");

        // Wait for 250ms.
        await new Promise(resolve => setTimeout(resolve, 250));

        // Allow player input again
        this._flashingColor = false;
    }

    /**
     * Handle the player inputting a color
     * @param color The color the player inputted
     */
    private async handlePlayerInput(color: string): Promise<void> {
        // Dont allow the player to input colors while a color is flashing
        if (this._flashingColor) {
            return;
        }

        // Dont allow the player to input colors if the sequence is not generated yet
        if (this._sequence.length === 0) {
            return;
        }

        this._playerSequence.push(color);

        // Check if the last color the player inputted is the same as the last color in the sequence. If not the player lost
        if (this._playerSequence[this._playerSequence.length - 1] !== this._sequence[this._playerSequence.length - 1]) {
            this.loseGame();
            return;
        }

        // Flash the button that the player clicked to show that it was registered
        await this.flashColor(color);

        // If the player is not done with the sequence wait for the next input before checking
        if (this._playerSequence.length !== this._currentStep) {
            return;
        }

        // We completed the final step in the sequence and won the game
        if (this._currentStep === this.steps) {
            await this.winGame();
            return;
        }

        // Reset the player sequence and move on to the next step
        this._playerSequence = [];
        this._currentStep++;

        // Wait for 500ms and then play the sequence again
        setTimeout(() => this.playSequence(), 500);
    }

    /**
     * Reset the puzzle to the initial state to play again
     */
    private resetGame(): void {
        this.generateSequence();

        // Reset the sequence the player inputted and the current step
        this._playerSequence = [];
        this._currentStep = 1;

        if (this._statusParagraph) {
            this._statusParagraph.innerText = "";
        }

        void this.playSequence();
    }

    /**
     * Handle the player losing the game
     */
    private loseGame(): void {
        if (this._statusParagraph) {
            this._statusParagraph.innerText = "Je hebt verloren! Probeer het opnieuw.";
        }
    }

    /**
     * Handle the player winning the game
     */
    private async winGame(): Promise<void> {
        const state: GameState | undefined = await this._gameRouteService.executePuzzleCompletion(this._puzzleAlias);

        if (state) {
            this._gameEventService.updateGameState(state);
        }
        else {
            console.error("Er ging iets miss bij het updaten van de puzzle state");
        }
    }
}
